import { useEffect, useState } from "react";
import { usePlant } from "@/lib/context";
import api from "@/lib/api";
import { num, intf, statusColor, COLORS } from "@/lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, ReferenceLine, Cell } from "recharts";

function MiniGauge({ value, label, target, max = 100, testid }) {
  const norm = Math.max(0, Math.min(1, value / max));
  const color = value < target ? COLORS.red : value < target * 1.15 ? COLORS.yellow : COLORS.green;
  return (
    <div className="panel p-4" data-testid={testid}>
      <div className="label-small">{label}</div>
      <div className="metric-mid mt-2" style={{ color }}>{num(value, 1)}<span className="text-sm ml-1">%</span></div>
      <div className="mt-2 h-2 bg-[#14151C] relative">
        <div className="absolute h-full" style={{ width: `${norm * 100}%`, background: color }} />
        <div className="absolute h-full border-r border-[#00E5FF]" style={{ width: `${(target / max) * 100}%` }} />
      </div>
      <div className="text-[10px] mt-1 text-[#8F93A2] font-mono">target {target}%</div>
    </div>
  );
}

export default function Furnace() {
  const { state } = usePlant();
  const [heats, setHeats] = useState([]);
  const [snaps, setSnaps] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [h, s] = await Promise.all([
          api.get("/plant/heats?limit=20"),
          api.get("/plant/snapshots?hours=2"),
        ]);
        if (alive) {
          setHeats(h.data.heats || []);
          setSnaps(s.data.snapshots || []);
        }
      } catch {}
    };
    load();
    const id = setInterval(load, 8000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!state) return <div className="p-10 text-[#8F93A2]">Loading…</div>;
  const m = state.metrics || {};

  const lossWaterfall = [
    { name: "Theoretical", value: m.ce_04 || 244, fill: COLORS.blue },
    { name: "Refractory", value: m.ce_10 || 80, fill: "#a05a2c" },
    { name: "Coil Cooling", value: Math.min(80, m.ce_11 || 0), fill: COLORS.amber },
    { name: "Idle/Holding", value: m.ce_12 || 0, fill: COLORS.green },
    { name: "Charge", value: m.ce_13 || 0, fill: COLORS.green },
    { name: "PF Electrical", value: m.ce_14 || 0, fill: COLORS.yellow },
    { name: "Oxidation", value: m.ce_15 || 0, fill: COLORS.red },
  ];

  const powerData = snaps.map((s) => ({
    t: new Date(s.plant_time).toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit" }),
    F1: s.tags?.FURN1_ACTIVE_KW || 0,
    F2: s.tags?.FURN2_ACTIVE_KW || 0,
  }));

  const coilData = snaps.map((s) => ({
    t: new Date(s.plant_time).toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit" }),
    F1: s.tags?.FURN1_COIL_OUT_TEMP || 38,
    F2: s.tags?.FURN2_COIL_OUT_TEMP || 38,
  }));

  return (
    <div className="space-y-5">
      <div>
        <div className="label-small">Furnace Intelligence</div>
        <h1 className="font-display text-3xl uppercase tracking-tight font-bold">Two-Furnace Energy Picture</h1>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-3">
          <MiniGauge value={m.ce_05 || 0} label="Furnace Eff F1" target={35} testid="gauge-eff-f1" />
        </div>
        <div className="col-span-12 md:col-span-3">
          <MiniGauge value={m.ce_05 || 0} label="Furnace Eff F2" target={35} testid="gauge-eff-f2" />
        </div>
        <div className="col-span-12 md:col-span-6 panel p-4">
          <div className="label-small">Refractory Health (F1)</div>
          <div className="text-xs text-[#8F93A2] mt-1">Coil ΔT trending — rising indicates lining degradation</div>
          <div className="metric-mid mt-2 text-[#00E5FF]">
            {num((state.tags?.FURN1_COIL_OUT_TEMP - state.tags?.FURN1_COIL_IN_TEMP) || 0, 1)}<span className="text-sm ml-1">°C ΔT</span>
          </div>
          <div className="mt-2 text-[11px] text-[#8F93A2]">
            Refractory loss component: <span className="font-mono text-white">{num(m.ce_10, 0)} kWh/t</span>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 panel">
          <div className="panel-header"><span>Live Furnace Power Curve (last 2h)</span></div>
          <div className="p-3 h-[280px]" data-testid="furnace-power-chart">
            <ResponsiveContainer>
              <LineChart data={powerData}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="t" stroke={COLORS.textDim} fontSize={10} />
                <YAxis stroke={COLORS.textDim} fontSize={10} domain={[0, 700]} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <ReferenceLine y={550} stroke={COLORS.green} strokeDasharray="3 3" />
                <ReferenceLine y={192} stroke={COLORS.red} strokeDasharray="3 3" />
                <Line type="monotone" dataKey="F1" stroke={COLORS.blue} dot={false} strokeWidth={2}  isAnimationActive={false} />
                <Line type="monotone" dataKey="F2" stroke={COLORS.amber} dot={false} strokeWidth={2}  isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 panel">
          <div className="panel-header"><span>Loss Waterfall — kWh/tonne</span></div>
          <div className="p-3 h-[280px]" data-testid="loss-waterfall-chart">
            <ResponsiveContainer>
              <BarChart data={lossWaterfall} layout="horizontal">
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke={COLORS.textDim} fontSize={9} angle={-15} textAnchor="end" height={50} />
                <YAxis stroke={COLORS.textDim} fontSize={10} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <Bar dataKey="value" isAnimationActive={false}>
                  {lossWaterfall.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 panel">
          <div className="panel-header"><span>Heat Log — last 20 heats</span><span className="text-[10px] text-[#8F93A2]">Sortable · Exportable</span></div>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm" data-testid="heat-log-table">
              <thead className="text-[#8F93A2] uppercase tracking-wider text-[10px]">
                <tr className="border-b border-[#1F212A]">
                  <th className="text-left p-2">Heat #</th>
                  <th className="text-left p-2">F</th>
                  <th className="text-left p-2">Start</th>
                  <th className="text-right p-2">T2T (min)</th>
                  <th className="text-right p-2">Charge (kg)</th>
                  <th className="text-right p-2">CQS</th>
                  <th className="text-right p-2">kWh</th>
                  <th className="text-right p-2">SEC kWh/t</th>
                  <th className="text-right p-2">Tap (°C)</th>
                  <th className="text-right p-2">Yield %</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                {heats.map((h, idx) => (
                  <tr key={h.id || `heat-${h.furnace_id}-${h.heat_number}-${idx}`} className="border-b border-[#14151C] hover:bg-[#14151C]" data-testid={`heat-row-${h.heat_number}`}>
                    <td className="p-2">{h.heat_number}</td>
                    <td className="p-2">F{h.furnace_id}</td>
                    <td className="p-2 text-[#8F93A2]">{new Date(h.start_plant_time).toLocaleTimeString("en-IN", { hour12: false })}</td>
                    <td className={`p-2 text-right ${h.tap_to_tap_min > 40 ? "text-[#FF3333]" : h.tap_to_tap_min > 32 ? "text-[#FFCC00]" : "text-[#00FF66]"}`}>{num(h.tap_to_tap_min, 1)}</td>
                    <td className="p-2 text-right">{intf(h.charge_total_kg)}</td>
                    <td className={`p-2 text-right ${h.cqs < 60 ? "text-[#FF3333]" : h.cqs < 75 ? "text-[#FFCC00]" : "text-[#00FF66]"}`}>{num(h.cqs, 0)}</td>
                    <td className="p-2 text-right">{intf(h.kwh_this_heat)}</td>
                    <td className={`p-2 text-right ${h.sec_kwh_per_t > 850 ? "text-[#FF3333]" : "text-white"}`}>{num(h.sec_kwh_per_t, 0)}</td>
                    <td className="p-2 text-right">{num(h.tap_temperature_c, 0)}</td>
                    <td className={`p-2 text-right ${h.yield_pct < 70 ? "text-[#FF3333]" : h.yield_pct > 75 ? "text-[#00FF66]" : "text-[#FFCC00]"}`}>{num(h.yield_pct, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-span-12 panel">
          <div className="panel-header"><span>Coil Cooling Outlet Trend (last 2h)</span></div>
          <div className="p-3 h-[180px]" data-testid="coil-trend-chart">
            <ResponsiveContainer>
              <LineChart data={coilData}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="t" stroke={COLORS.textDim} fontSize={10} />
                <YAxis stroke={COLORS.textDim} fontSize={10} domain={[30, 60]} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <ReferenceLine y={42} stroke={COLORS.yellow} strokeDasharray="3 3" label={{ value: "Watch", fill: COLORS.yellow, fontSize: 10 }} />
                <Line type="monotone" dataKey="F1" stroke={COLORS.blue} dot={false} strokeWidth={2}  isAnimationActive={false} />
                <Line type="monotone" dataKey="F2" stroke={COLORS.amber} dot={false} strokeWidth={2}  isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
